/**
 * Carte de progression.
 *
 * Affiche trois choses utiles et rien de plus : où en est le transfert, à quelle vitesse,
 * et dans combien de temps. La vitesse et l'estimation viennent de mesures réelles
 * (`performance.now()` et octets traités), pas d'une animation décorative.
 */

import { useFormatters, useT } from '../i18n/index.js';
import { useAppStore } from '../store.js';

interface ProgressCardProps {
  title: string;
  onCancel: () => void;
}

export function ProgressCard({ title, onCancel }: ProgressCardProps): React.JSX.Element {
  const progress = useAppStore((state) => state.progress);
  const translator = useT();
  const format = useFormatters();

  if (!progress) {
    return (
      <div className="card">
        <h2>{title}</h2>
        <p className="muted">{translator.t('progress.preparing')}</p>
      </div>
    );
  }

  const overallPercent = percent(progress.totalBytes, progress.totalTotal);
  const filePercent = percent(progress.fileBytes, progress.fileTotal);

  return (
    <div className="card">
      <div className="progress-head">
        <h2 style={{ margin: 0 }}>{title}</h2>
        <span className="faint">
          {progress.fileCount > 1
            ? translator.t('progress.fileOfTotal', {
                index: progress.fileIndex + 1,
                count: progress.fileCount,
              })
            : format.bytes(progress.totalTotal)}
        </span>
      </div>

      <div
        className="progress-track"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(overallPercent)}
        aria-label={title}
      >
        <div className="progress-fill" style={{ width: `${overallPercent}%` }} />
      </div>

      <div className="progress-stats">
        <span>
          <b>{format.number(Number(overallPercent.toFixed(1)))} %</b> ·{' '}
          {translator.t('progress.overall', {
            percent: format.number(Number(overallPercent.toFixed(1))),
          })}
        </span>
        <span>
          <b>{format.bytes(progress.totalBytes)}</b> / {format.bytes(progress.totalTotal)}
        </span>
        <span>
          <b>{format.speed(progress.bytesPerSecond)}</b>
        </span>
        {progress.etaSeconds !== null && progress.etaSeconds > 1 && (
          <span>
            {translator.t('progress.remaining', { duration: format.duration(progress.etaSeconds) })}
          </span>
        )}
      </div>

      {progress.fileCount > 1 && (
        <p className="faint" style={{ marginBottom: 0 }}>
          {progress.fileName} — {format.number(Number(filePercent.toFixed(0)))} %
        </p>
      )}

      <p style={{ marginBottom: 0, marginTop: '1rem' }}>
        <button type="button" className="btn btn-sm btn-danger" onClick={onCancel}>
          {translator.t('progress.cancel')}
        </button>
      </p>
    </div>
  );
}

function percent(done: number, total: number): number {
  if (total <= 0) return 0;
  return Math.min(100, Math.max(0, (done / total) * 100));
}
